// Single source of truth for every service offered by Schaefer Services.
// Each service has its own page at /services/<slug>.

export const services = [
  {
    slug: "land-clearing",
    name: "Land Clearing",
    icon: "Tree",
    short:
      "Brush, trees, stumps and debris removed for a clean, buildable site.",
    long: [
      "Whether you're breaking ground on a new home, expanding pasture, or finally clearing that back acreage you've never been able to use — we handle it start to finish.",
      "We remove brush, cedars, mesquites, overgrowth, root balls and debris, then haul it off or burn on-site (where permitted). You end up with a clean, usable piece of property, ready for grading, fencing, building or farming.",
    ],
    includes: [
      "Brush, scrub and overgrowth removal",
      "Cedar, mesquite and unwanted tree removal",
      "Stump grinding and root removal",
      "Debris haul-off or controlled burns where allowed",
      "Rough grading to leave a usable surface",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_schaefer-services/artifacts/w7r0rso6_IMG_1926.jpg",
  },
  {
    slug: "grading",
    name: "Grading",
    icon: "Ruler",
    short:
      "Precision grading for drainage, foundations and finish work.",
    long: [
      "Grading is the foundation of every good project. Done right, water flows where it should, buildings sit square, and driveways last. Done wrong, you're fighting erosion, cracked slabs and drainage nightmares for years.",
      "We grade new building sites, fix drainage problems around existing homes and shops, shape pads for concrete, slope yards away from foundations, and finish off landscaping projects clean and ready to seed.",
    ],
    includes: [
      "Building site grading to spec",
      "Drainage correction around homes, shops and barns",
      "Front, side and back yard finish grading",
      "Slope and crown work for water runoff",
      "Fine grading prior to concrete or seed",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_schaefer-services/artifacts/cms98uli_IMG_0765.jpg",
  },
  {
    slug: "pad-building",
    name: "Pad Building",
    icon: "SquaresFour",
    short:
      "Engineered building pads for homes, barns, shops and metal buildings.",
    long: [
      "A good building starts with a good pad. We build compacted, graded pads sized and engineered for whatever you're putting on top — house, barndominium, shop, equipment barn, RV cover or storage building.",
      "We source and haul in the right fill material, place it in lifts, compact to spec, crown it properly for drainage, and leave you a pad your builder or slab crew can go straight to work on.",
    ],
    includes: [
      "Site layout, stake-out and cut/fill planning",
      "Fill material hauling (select fill, caliche, base)",
      "Compacted lifts built to engineer / builder spec",
      "Finish-graded crown and slope for water management",
      "Proof-rolled and ready for plumbing / slab crews",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_schaefer-services/artifacts/w7r0rso6_IMG_1926.jpg",
  },
  {
    slug: "fence-work",
    name: "Fence Work",
    icon: "GridFour",
    short:
      "New fence installation — barbed wire, pipe, ranch and privacy.",
    long: [
      "From pasture fences to perimeter pipe fence to the back-yard privacy fence — we install fence that stands up to Texas weather, cattle, brush and time.",
      "We clear the line, set posts in concrete, stretch wire tight, square corners properly, and leave you a fence that does its job.",
    ],
    includes: [
      "Barbed wire and field fence for pasture and ranch",
      "Pipe fence and pipe top-rail entrances",
      "Residential privacy, picket and rail",
      "Corner bracing, H-braces and gates",
      "Cattle guards, gates and access points",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_schaefer-services/artifacts/i42fqa44_IMG_2633.jpeg",
  },
  {
    slug: "fence-repairs",
    name: "Fence Repairs",
    icon: "Wrench",
    short:
      "Storm damage, sagging lines, broken posts — back up in no time.",
    long: [
      "Busted posts after a storm? Wire stretched out by cattle? Gate falling off its hinges? We'll come out, figure out what's wrong, and fix it without making you replace the whole fence.",
      "We repair and re-tension barbed, field and privacy fencing, replace broken posts and rotten wood, re-hang gates and patch sections so the new work ties into the old cleanly.",
    ],
    includes: [
      "Post replacement (wood, T-post, pipe)",
      "Wire re-stretching and tensioning",
      "Storm and fallen-tree damage repair",
      "Gate re-hang and hinge replacement",
      "Fence line clean-up and brush removal",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_schaefer-services/artifacts/8fp768xw_IMG_2630.jpeg",
  },
  {
    slug: "tree-trimming",
    name: "Tree Trimming",
    icon: "Scissors",
    short:
      "Healthy, safe tree maintenance for trees big and small.",
    long: [
      "Trimming keeps trees healthy, clears limbs off your roof and power lines, opens up views, and keeps driveways and fences clear.",
      "We trim residential and ranch trees, lift canopies for mowing, remove dead or crossing limbs, and clean up every bit of the debris before we leave.",
    ],
    includes: [
      "Canopy lifting and shaping",
      "Dead and hazard limb removal",
      "Clearance trimming (roofs, fences, drives)",
      "Pasture and fence-line tree work",
      "Full haul-off of cuttings and debris",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_schaefer-services/artifacts/w7r0rso6_IMG_1926.jpg",
  },
  {
    slug: "tree-removal",
    name: "Tree Removal",
    icon: "TreeStructure",
    short:
      "Safe removal of hazardous or unwanted trees — stumps included.",
    long: [
      "Dead trees, leaners, trees too close to a house, or just an overgrown section you want cleared — we take them down safely and clean.",
      "We remove the tree, grind or pull the stump, haul off the wood, and rake the area so it's ready to mow or seed.",
    ],
    includes: [
      "Residential and ranch tree removal",
      "Hazard and leaning-tree takedowns",
      "Stump grinding or full stump removal",
      "Wood and brush haul-off",
      "Site clean-up and finish rake",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_schaefer-services/artifacts/w7r0rso6_IMG_1926.jpg",
  },
  {
    slug: "pergolas",
    name: "Pergolas",
    icon: "HouseLine",
    short:
      "Custom-built pergolas that anchor your backyard.",
    long: [
      "A pergola turns a plain slab or patch of grass into the best spot on the property. We build custom wood and metal pergolas sized for your yard, shade needs and budget.",
      "From simple 4-post structures to larger covered entertaining pergolas with lights, fans and stained beams — we build it on-site, square, level and solid.",
    ],
    includes: [
      "Site layout and footing / pier work",
      "Cedar, pine or metal post and beam construction",
      "Stain, paint and finish options",
      "Tie-ins to existing patio, slab or deck",
      "Optional lighting and fan rough-ins",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_schaefer-services/artifacts/585hpeg0_20210517_125727~2_Original.jpeg",
  },
  {
    slug: "deck-construction",
    name: "Deck Construction",
    icon: "Stack",
    short:
      "Solid, weather-tough decks built to Texas standards.",
    long: [
      "A deck should last. We build decks on pier or concrete footings with treated framing and your choice of decking — treated pine, cedar, or composite.",
      "We handle railing, steps, built-in benches, and proper flashing against the house so you don't end up with rot behind the ledger board two years in.",
    ],
    includes: [
      "Pier and beam or concrete footing foundations",
      "Treated framing with joist tape / flashing",
      "Treated, cedar or composite decking",
      "Railings, steps and built-ins",
      "Stain or sealant finish options",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_schaefer-services/artifacts/cms98uli_IMG_0765.jpg",
  },
  {
    slug: "driveway-construction",
    name: "Driveway Repairs & Construction",
    icon: "Path",
    short:
      "Gravel, caliche and base — from new drives to potholes patched.",
    long: [
      "A well-built driveway is invisible when it's right and a daily frustration when it's not. We build new driveways from the sub-base up with the right materials for your soil and traffic, and we repair and resurface tired drives that have washed out, potholed or gone muddy.",
      "Gravel, caliche, base rock, shaped crown and proper drainage — we do what it takes to give you a drive that holds up to pickups, trailers, service trucks and Texas rain.",
    ],
    includes: [
      "New driveway construction from grade up",
      "Caliche, base rock, gravel and road base placement",
      "Pothole repair and resurfacing",
      "Culvert installation and drainage correction",
      "Shaping, crowning and compaction",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_schaefer-services/artifacts/l73piqdw_IMG_0768.jpg",
  },
  {
    slug: "private-road",
    name: "Private Road Maintenance & Construction",
    icon: "RoadHorizon",
    short:
      "Grading, resurfacing and drainage for ranch and private roads.",
    long: [
      "Ranch roads, shared driveways and private subdivision roads don't get county maintenance — that's where we come in.",
      "We build new private roads and keep existing ones in shape with regular grading, crown work, drainage cleanup, base rock top-ups and pothole repair. Whether you need a one-time re-grade or an ongoing maintenance schedule, we'll work out a plan.",
    ],
    includes: [
      "New private road construction",
      "Regular grading and crown restoration",
      "Base rock / caliche top-up",
      "Ditch and culvert cleaning",
      "Ongoing maintenance agreements available",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_schaefer-services/artifacts/l73piqdw_IMG_0768.jpg",
  },
  {
    slug: "pond-work",
    name: "Pond Cleaning & Construction",
    icon: "Drop",
    short:
      "New ponds dug and old ponds cleaned out — ready for water.",
    long: [
      "We build new stock tanks and ponds sized for your property and water needs, and we clean out old ponds that have silted in, grown over or lost their capacity.",
      "From site selection and dam building to bringing an old tank back to holding water — we've got the iron and the know-how to do it right.",
    ],
    includes: [
      "New pond / stock tank construction",
      "Dam building and spillway shaping",
      "Silt removal and pond clean-out",
      "Shoreline and bank work",
      "Watering and wildlife habitat considerations",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_schaefer-services/artifacts/2qoyhqbm_IMG_1129.jpg",
  },
  {
    slug: "general-dirt-work",
    name: "General Dirt Work",
    icon: "Shovel",
    short:
      "If it involves dirt, we can move it, shape it, or haul it.",
    long: [
      "Got a drainage problem, an eyesore of a dirt pile, a trench to cut, a lot to level, a swale to re-shape, or dirt to haul in or out? That's us.",
      "If your project doesn't fit neatly into a category but it involves a skid steer, tractor, backhoe or dump truck — just call. Odds are we've already done something like it.",
    ],
    includes: [
      "Lot leveling and yard re-shaping",
      "Trenching for utilities, drains and water lines",
      "Dirt hauling — bring in or haul off",
      "Backfill and compaction work",
      "Site clean-up and final rake",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_schaefer-services/artifacts/4i18oxku_IMG_0867.jpg",
  },
  {
    slug: "heavy-equipment-service",
    name: "Heavy Equipment Service & Repair",
    icon: "Gear",
    short:
      "Field service and repairs to keep your iron running.",
    long: [
      "When equipment goes down, every hour costs money. We do field service and repair on skid steers, tractors, backhoes, excavators, dump trucks and trailers — so your operation doesn't stop.",
      "Preventive maintenance, hydraulic repairs, electrical, engine diagnostics, brakes, tires and trailer work. Mobile service to your job site or shop.",
    ],
    includes: [
      "On-site / field service calls",
      "Preventive maintenance and fluid changes",
      "Hydraulic and electrical repairs",
      "Engine and drivetrain diagnostics",
      "Trailer brakes, bearings, lights and tires",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_schaefer-services/artifacts/je91iggj_IMG_0375.jpg",
  },
  {
    slug: "concrete",
    name: "Concrete Repairs & Construction",
    icon: "Cube",
    short:
      "New concrete poured clean and old concrete brought back to life.",
    long: [
      "We handle concrete from form to finish — new slabs, driveways, sidewalks, equipment pads, steps, aprons and patios — plus repairs to cracked, settled or broken existing concrete.",
      "Whether it's a fresh pour prepped from the sub-grade up or cutting out and replacing a damaged section, we do the prep, the forms, the pour and the finish.",
    ],
    includes: [
      "New slabs, driveways, sidewalks and patios",
      "Equipment and shop pads",
      "Steps, aprons and approach work",
      "Crack, settlement and broken-section repair",
      "Saw-cutting, removal and replacement",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_schaefer-services/artifacts/2qoyhqbm_IMG_1129.jpg",
  },
  {
    slug: "non-cdl-services",
    name: "Non-CDL Hauling & Transport Services",
    icon: "Truck",
    short:
      "Hauling, equipment moves and light transport — no CDL job required.",
    long: [
      "Need a skid steer moved across town? A small load of gravel, base rock or fill dropped at your place? A piece of equipment picked up from the dealer? We handle non-CDL hauling and transport jobs that bigger commercial outfits won't bother with.",
      "With the right truck, gooseneck and flatbed setup under 26,000 lbs GVWR, we can get to your site, load up, and deliver — quick and affordable.",
    ],
    includes: [
      "Skid steer and small equipment transport",
      "Gravel, base rock, caliche and fill delivery",
      "Trailer and implement pick-up / drop-off",
      "Light demolition debris haul-off",
      "On-demand same-day or scheduled runs",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_schaefer-services/artifacts/je91iggj_IMG_0375.jpg",
  },
];

export const getServiceBySlug = (slug) =>
  services.find((s) => s.slug === slug) || null;
